import { useId, useRef } from 'react';

// Outclose loading animation. The keyframes + base classes live once, inline in
// client/index.html's <head>, so they paint instantly as the boot splash (the
// markup mirrored there in #root) AND are globally available to this component.
// This component additionally:
//  - gives each instance a unique mask id (duplicate SVG ids break the center
//    hole when several loaders render at once), and
//  - anchors every instance to the shared performance.now() clock via a negative
//    animation-delay, so loaders stay in phase with one another and resume
//    mid-animation on (re)mount instead of restarting from frame 0.

const SPIN_MS = 3200; // .app-loader spin duration (3.2s)
const PULSE_MS = 1100; // lobe pulse duration (1.1s)

// Lobes in render order with their clockwise wave stagger (matches index.html).
const LOBES = [
  { rotate: 15, delay: 0 },
  { rotate: -195, delay: 0.25 * PULSE_MS },
  { rotate: -125, delay: 0.5 * PULSE_MS },
  { rotate: -55, delay: 0.75 * PULSE_MS },
];

const LOBE_PATH = 'M 38,40 C 38,32 30,22 32,13 C 34,4 66,4 68,13 C 70,22 62,32 62,40 Z';

interface LoaderProps {
  /** Width/height in px. Defaults to 72 (the standard centered size). */
  size?: number;
  className?: string;
}

export function Loader({ size = 72, className }: LoaderProps) {
  // useId() can contain ':', which is awkward inside url(#...) — strip it.
  const maskId = `app-loader-${useId().replace(/:/g, '')}`;

  // Lock the phase anchor once so re-renders don't restart the animation.
  const nowRef = useRef<number | null>(null);
  if (nowRef.current === null) {
    nowRef.current = typeof performance !== 'undefined' ? performance.now() : 0;
  }
  const now = nowRef.current;

  return (
    <svg
      className={`app-loader${className ? ` ${className}` : ''}`}
      style={{ animationDelay: `${-(now % SPIN_MS)}ms` }}
      width={size}
      height={size}
      viewBox="-15 -15 130 130"
      role="status"
      aria-label="Loading"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <mask id={maskId}>
          <rect x="-15" y="-15" width="130" height="130" fill="white" />
          <circle cx="50" cy="50" r="16" fill="black" />
        </mask>
      </defs>
      <g mask={`url(#${maskId})`} fill="currentColor">
        <circle cx="50" cy="50" r="32" />
        {LOBES.map((lobe, i) => (
          <g key={i} className="app-loader-lobe" transform={`rotate(${lobe.rotate} 50 50)`}>
            <path d={LOBE_PATH} style={{ animationDelay: `${lobe.delay - (now % PULSE_MS)}ms` }} />
          </g>
        ))}
      </g>
    </svg>
  );
}
